import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useInvoiceFeature } from '../../hooks/useInvoiceFeature';
import { useCardFeature } from '../../hooks/useCardFeature';
import { isInvoiceProfileComplete } from '../../utils/invoiceProfile';
import { stripeApi } from '../../firebase/functionsService';
import { connectPanelView } from './connectPanelView';
import StripeConnectPanel from './StripeConnectPanel';
import { COLORS, FONTS } from '../../styles/tokens';

const TONE = {
  neutral: [COLORS.textMid, COLORS.surfaceHover], warning: [COLORS.warningText, COLORS.warningBg],
  success: [COLORS.successText, COLORS.successBg], danger: [COLORS.dangerText, COLORS.dangerBg],
};

// «Slik får du betalt»: faktura via Tippi er standard, Stripe er et nedtonet, valgfritt alternativ.
export default function PayMethodBlock({ hasCardEarnings = false }) {
  const navigate = useNavigate();
  const { userDoc } = useAuth();
  const { enabled } = useInvoiceFeature();
  const { enabled: cardEnabled } = useCardFeature();
  const [stripeState, setStripeState] = useState(null);
  const [open, setOpen] = useState(false);

  // Status hentes alltid (kun for chip og varsel); panelet monteres først når raden åpnes.
  useEffect(() => {
    let cancelled = false;
    stripeApi.getConnectStatus()
      .then(r => { if (!cancelled) setStripeState(r?.state ?? null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const profileComplete = isInvoiceProfileComplete(userDoc?.invoiceProfile);
  const view = stripeState ? connectPanelView(stripeState) : null;
  const connected = stripeState === 'active';
  // Stripe vises ikke når kort er av, med mindre sjåføren har kortinntekter til gode.
  const showStripe = cardEnabled || hasCardEarnings;
  const showPayoutNotice = hasCardEarnings && stripeState && !connected;

  // Sjåfører i et transportfirma får betalt via firmaet, ikke via Stripe eller egen faktura
  if (userDoc?.carrierOrgId) return null;

  return (
    <section style={styles.block} aria-label="Slik får du betalt">
      <h2 style={styles.title}>Slik får du betalt</h2>

      <div style={styles.invoiceRow}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.rowHead}>
            <span style={styles.rowTitle}>Faktura via Tippi</span>
            <span style={styles.recommended}>Anbefalt</span>
          </div>
          <div style={styles.muted}>Du fakturerer kunden direkte via Tippi etter hvert oppdrag.</div>
        </div>
        {profileComplete ? (
          <span style={styles.ready}>Klar</span>
        ) : (
          <button style={styles.primaryBtn} onClick={() => navigate('/driver/profil#faktura')}>
            {enabled ? 'Fyll ut fakturaopplysninger' : 'Fyll ut nå'}
          </button>
        )}
        {!enabled && (
          <p style={styles.explain}>
            <b>Åpner snart.</b> Tippi slår på fakturering for alle når den er klar, og du trenger ikke gjøre noe for
            det. {profileComplete
              ? 'Utbetaling og fakturering er allerede fylt ut, så du er klar når den åpner.'
              : 'Fyll gjerne ut utbetaling og fakturering i profilen nå, så er du klar når den åpner.'}
          </p>
        )}
      </div>

      {showStripe && (
      <div style={styles.stripeRow}>
        <div style={styles.stripeHead}>
          <span style={styles.stripeTitle}>Stripe-utbetaling (valgfritt)</span>
          {connected && view && (
            <span style={{ ...styles.chip, color: TONE[view.tone][0], background: TONE[view.tone][1] }}>{view.badge}</span>
          )}
          <button
            style={styles.linkBtn}
            aria-expanded={open}
            aria-controls="stripe-panel"
            onClick={() => setOpen(o => !o)}
          >
            {open ? 'Skjul' : connected ? 'Åpne' : 'Koble til'}
          </button>
        </div>
        {open && <div id="stripe-panel"><StripeConnectPanel embedded /></div>}
      </div>
      )}

      {showPayoutNotice && (
        <p role="status" style={styles.notice}>
          Du har kortbetalte oppdrag som venter på utbetaling. Koble til Stripe for å få dem utbetalt.
        </p>
      )}
    </section>
  );
}

const styles = {
  block:       { background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: '12px 14px', marginBottom: 14 },
  title:       { fontFamily: FONTS.display, fontSize: 18, fontWeight: 700, color: COLORS.textDark, margin: '0 0 8px' },
  invoiceRow:  { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 12px', borderRadius: 12, border: `2px solid ${COLORS.accent}`, background: COLORS.bg },
  rowHead:     { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  rowTitle:    { fontWeight: 700, fontSize: 15, color: COLORS.textDark },
  recommended: { fontSize: 11, fontWeight: 700, borderRadius: 20, padding: '2px 8px', background: COLORS.primary, color: COLORS.onPrimary },
  muted:       { fontSize: 12.5, color: COLORS.textMid, marginTop: 3 },
  ready:       { fontSize: 12.5, fontWeight: 700, borderRadius: 20, padding: '4px 12px', color: COLORS.successText, background: COLORS.successBg },
  explain:     { flexBasis: '100%', margin: '2px 0 0', fontSize: 12.5, lineHeight: 1.4, color: COLORS.textMid },
  primaryBtn:  { padding: '8px 14px', borderRadius: 10, border: 'none', background: COLORS.primary, color: COLORS.onPrimary, fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  stripeRow:   { marginTop: 8, padding: '4px 4px 0' },
  stripeHead:  { display: 'flex', alignItems: 'center', gap: 8 },
  stripeTitle: { flex: 1, fontSize: 13, color: COLORS.textMid },
  chip:        { fontSize: 11, fontWeight: 700, borderRadius: 20, padding: '2px 8px', whiteSpace: 'nowrap' },
  linkBtn:     { background: 'none', border: 'none', color: COLORS.textMid, textDecoration: 'underline', fontSize: 13, cursor: 'pointer', padding: '6px 4px' },
  notice:      { fontSize: 12.5, color: COLORS.warningText, background: COLORS.warningBg, borderRadius: 10, padding: '8px 10px', margin: '8px 0 0' },
};
